/**
 * Single source of truth for "which brand is this frontend build/runtime
 * treated as" — the frontend counterpart of
 * backend/src/modules/brand-config/brand-resolver.service.ts.
 *
 * Used by the API client to send X-Brand-Id so login can resolve the
 * company tenant. Later phases (domain-based branding) can keep using
 * this same resolver instead of every consumer re-implementing lookup.
 *
 * Resolution order (first match wins):
 *   1. window.__BRAND_ID__   — a runtime override a host page/shell could
 *      set before the bundle loads (e.g. a future domain-based loader
 *      that maps a hostname to a brand id). Nothing sets this today, so
 *      this tier is inert until something does.
 *   2. REACT_APP_BRAND_ID    — today's build-time mechanism, written by
 *      scripts/apply-brand.ps1 from the active brand's brand.json (see
 *      src/config/brand.js, which already reads the same env var).
 *   3. "ess"                 — fail-open default, matching the brand this
 *      codebase already ships as for the current production system.
 *
 * Never returns null/empty/undefined.
 */
export const DEFAULT_BRAND_ID = "ess";

function normalize(value) {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }
  return value.trim();
}

function readWindowBrandId() {
  if (typeof window === "undefined") {
    return null;
  }
  return normalize(window.__BRAND_ID__);
}

function readEnvBrandId() {
  return normalize(process.env.REACT_APP_BRAND_ID);
}

export function resolveBrandId() {
  const fromWindow = readWindowBrandId();
  if (fromWindow) {
    return fromWindow;
  }

  const fromEnv = readEnvBrandId();
  if (fromEnv) {
    return fromEnv;
  }

  return DEFAULT_BRAND_ID;
}

export default resolveBrandId;
